import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVideoCall, CallStatus } from '../context/VideoCallContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Camera, MapPin, Briefcase, Award, Calendar, ArrowLeft, Edit2, Save, X, TrendingUp, Users, Gift, Star, FileText, Download, Video, Loader2, Hash, Copy, Shield, LogOut } from 'lucide-react';
import { INDUSTRIES, INDUSTRY_DOMAINS, US_STATES } from '../constants/industries';
import ScheduleMeetingDialog from '../components/ScheduleMeetingDialog';
import { usePageHeader } from '../context/PageHeaderContext';

// Logo image URL for background (always use dark mode logo on black background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_4f2b8787-9e39-421b-9343-dec62de2fe7e/artifacts/wcig1jf2_Untitled%20design%20%286%29.png";

const ProfilePage = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, api, updateUser, refreshUser, isFeatureEnabled, logout } = useAuth();
  const { initiateCall, callState, isInCall } = useVideoCall();
  const fileInputRef = useRef(null);
  
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  // iter175l — Photo deletion is now STAGED locally and only applied
  // to the server when the user clicks Save. Clicking the X-overlay
  // toggles this flag → the avatar visually reverts to the initials
  // fallback in real time, but if the user navigates away or clicks
  // Cancel, the actual photo on the server is preserved untouched.
  const [photoStagedForDelete, setPhotoStagedForDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  
  // Check if coming from My Team page
  const fromTeam = searchParams.get('from') === 'team';

  // Hoist the page title into the global app header so the desktop
  // brand label swaps to "Profile" instead of "The Uplift". The hero
  // banner already carries its own "Back" overlay for non-own profiles,
  // so we do not duplicate a back arrow into the global header here.
  usePageHeader({ title: 'Profile' }, []);
  
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    state: '',
    industry: '',
    industry_domain: '',
    personal_phone: '',
    orb_phone: '',
    email: '',
    orb_email: '',
    home_address: '',
    orb_address: '',
    preferred_first_name: '',
    title: '',
  });

  const isOwnProfile = !userId || userId === user?.id;
  const displayUser = isOwnProfile ? user : profileUser;
  // ORB phone / email / address / referral_id are HIDDEN until the
  // user is fully onboarded (per iter 96 spec). Admin/supervisor
  // viewing other profiles bypasses this gate so they can still
  // verify a candidate's assignment fields. The displayed user's
  // own onboarding state is the authoritative gate when own-profile.
  const showOrbAndReferralFields = (
    !isOwnProfile
      ? true
      : !!(displayUser?.is_onboarded || user?.role === 'admin' || user?.role === 'super_admin')
  );

  useEffect(() => {
    if (!isOwnProfile && userId) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (displayUser) {
      setEditData({
        first_name: displayUser.first_name || '',
        last_name: displayUser.last_name || '',
        state: displayUser.state || '',
        industry: displayUser.industry || '',
        industry_domain: displayUser.industry_domain || '',
        personal_phone: displayUser.personal_phone || '',
        orb_phone: displayUser.orb_phone || '',
        email: displayUser.email || '',
        orb_email: displayUser.orb_email || '',
        home_address: displayUser.home_address || '',
        orb_address: displayUser.orb_address || '',
        preferred_first_name: displayUser.preferred_first_name || '',
        title: displayUser.title || '',
      });
    }
  }, [displayUser]);

  // iter 97 — when an ID has been verified, the legal name + DOB +
  // address fields are LOCKED on the profile (read-only). Only admins
  // can override (admins go through the user-edit drawer instead).
  const idLocked = !!(displayUser?.id_verified_at);
  const isAdminViewing = !isOwnProfile && (user?.role === 'admin' || user?.role === 'super_admin');

  const fetchUserProfile = async () => {
    try {
      const response = await api.get(`/users/${userId}`);
      setProfileUser(response.data);
    } catch (error) {
      toast.error('Failed to load user profile');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  // iter175k/l — STAGED delete. Toggles the `photoStagedForDelete`
  // flag so the avatar visually swaps to the initials fallback, but
  // the actual server-side photo is NOT touched until the user
  // clicks Save. If they Cancel or navigate away, nothing is lost.
  const handlePictureDelete = () => {
    if (!isOwnProfile) return;
    setPhotoStagedForDelete(true);
    // Reset the file input so re-picking the same file later still
    // fires the change event.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      // Compress image before upload
      const compressImage = (file) => {
        return new Promise((resolve) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          img.onload = () => {
            // Max dimensions
            const maxSize = 800;
            let width = img.width;
            let height = img.height;
            
            if (width > height && width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            
            // Return compressed base64 (JPEG at 80% quality)
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          };
          
          img.onerror = () => {
            // Fall back to original if compression fails
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(file);
          };
          
          // Load image from file
          const reader = new FileReader();
          reader.onload = (e) => { img.src = e.target.result; };
          reader.readAsDataURL(file);
        });
      };

      const base64 = await compressImage(file);
      console.log('Profile picture compressed, size:', base64.length, 'bytes');
      
      await updateUser({ profile_picture: base64 });
      await refreshUser();
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Profile picture upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // iter 97 — strip preferred_first_name from the main update;
      // it's saved through a dedicated endpoint so the backend can
      // separately validate / sanitize it.
      const { preferred_first_name, ...mainUpdate } = editData;
      // If ID-locked, don't even send first_name / last_name /
      // home_address (admins exempt — they edit those via /admin).
      if (idLocked && !isAdminViewing) {
        delete mainUpdate.first_name;
        delete mainUpdate.last_name;
        delete mainUpdate.home_address;
      }
      await updateUser(mainUpdate);
      // iter175l — Apply staged photo delete (if any) at Save-time.
      // Backend cascades to profile_picture_black_bg / _sig.
      if (photoStagedForDelete) {
        await updateUser({ profile_picture: null });
        setPhotoStagedForDelete(false);
      }
      // Save preferred name through dedicated endpoint
      const trimmedPref = (preferred_first_name || '').trim();
      const currentPref = (displayUser?.preferred_first_name || '').trim();
      if (trimmedPref !== currentPref) {
        await api.put('/users/me/preferred-name', {
          preferred_first_name: trimmedPref || null,
        });
      }
      await refreshUser();
      toast.success('Profile updated!');
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleVideoCall = async () => {
    if (!displayUser) return;
    
    try {
      await initiateCall(displayUser.id, 'video');
      toast.success(`Calling ${getFullName(displayUser)}...`);
    } catch (error) {
      console.error('Video call error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start video call');
    }
  };

  const getInitials = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.full_name || 'Unknown User';
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  const getOnboardingStatus = (user) => {
    if (user?.is_onboarded) return { label: 'Verified', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' };
    return { label: 'Onboarding', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' };
  };

  const availableDomains = editData.industry ? INDUSTRY_DOMAINS[editData.industry] || [] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-[#0a0a0a] dark:via-[#0d0d0d] dark:to-[#111] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <span className="text-muted-foreground text-sm">Loading profile...</span>
        </div>
      </div>
    );
  }

  if (!displayUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-[#0a0a0a] dark:via-[#0d0d0d] dark:to-[#111] flex items-center justify-center p-4">
        <Card className="max-w-sm w-full border-0 shadow-xl bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">User not found</p>
            <Button onClick={() => navigate('/search')} className="w-full">
              Back to Search
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = getOnboardingStatus(displayUser);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-[#0a0a0a] dark:via-[#0d0d0d] dark:to-[#111]" data-testid="profile-page">
      {/* Hero Section */}
      <div className="relative h-48 md:h-56 bg-black overflow-hidden">
        {/* Logo Background Image */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: '#000' }}
        >
          <img 
            src={LOGO_DARK} 
            alt="Background" 
            className="max-w-[80%] max-h-[80%] object-contain opacity-30"
          />
        </div>
        
        {/* Back Button */}
        {!isOwnProfile && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(fromTeam ? '/my-team' : '/search')}
            className="absolute top-4 left-4 text-white/90 hover:text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        {/* Edit Button */}
        {isOwnProfile && !editing && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setEditing(true); setPhotoStagedForDelete(false); }}
            className="absolute top-4 right-4 text-white/90 hover:text-white hover:bg-white/20"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        )}
        {isOwnProfile && editing && (
          <div className="absolute top-4 right-4 flex gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setEditing(false); setPhotoStagedForDelete(false); }}
              className="text-white/90 hover:text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              onClick={handleSaveProfile} 
              disabled={saving}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/* Avatar — sits ABOVE the gray card, overlapping the hero/ORB
          logo. Pulled out of the Card so the card itself can sit
          lower on the page. */}
      <div className="max-w-2xl mx-auto px-4 -mt-32 relative z-20 flex justify-center" data-testid="profile-avatar-block">
        <div className="relative">
          <Avatar key={(!photoStagedForDelete && displayUser?.profile_picture) ? 'photo' : 'no-photo'} className="w-28 h-40 rounded-2xl border-4 border-white dark:border-[#141414] shadow-xl ring-4 ring-white/50 dark:ring-[#141414]/50">
            {!photoStagedForDelete && (
              <AvatarImage
                src={displayUser.profile_picture}
                alt={getFullName(displayUser)}
                className="object-cover object-top"
                style={{ aspectRatio: 'auto' }}
              />
            )}
            <AvatarFallback className={`text-3xl font-bold bg-gradient-to-br ${getRoleGradient(displayUser.role)} text-white rounded-2xl`}>
              {getInitials(displayUser)}
            </AvatarFallback>
          </Avatar>
          {isOwnProfile && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handlePictureUpload}
              />
              <Button
                size="icon"
                className="absolute bottom-1 right-1 rounded-full w-9 h-9 shadow-lg bg-white dark:bg-[#1a1a1a] hover:bg-slate-50 dark:hover:bg-[#252525] text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                data-testid="profile-picture-upload-btn"
                aria-label="Upload profile picture"
                title="Upload profile picture"
              >
                <Camera className="w-4 h-4" />
              </Button>
              {/* iter175k — Delete-photo overlay. Visible only when the
                  user is in EDIT mode AND already has a profile pic.
                  Sits in the top-right corner of the avatar so it
                  doesn't collide with the bottom-right Camera button.
                  Same rounded-full pill aesthetic as the Camera FAB. */}
              {editing && displayUser?.profile_picture && !photoStagedForDelete && (
                <Button
                  size="icon"
                  className="absolute -top-2 -right-2 rounded-full w-7 h-7 shadow-lg bg-white dark:bg-[#1a1a1a] hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800"
                  onClick={handlePictureDelete}
                  disabled={uploadingPicture}
                  data-testid="profile-picture-delete-btn"
                  aria-label="Remove profile picture"
                  title="Remove profile picture"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Profile Content — now starts BELOW the hero (no negative
          margin) so the gray container sits clearly under the
          floating avatar */}
      <div className="max-w-2xl mx-auto px-4 mt-6 pb-24 relative z-10">
        {/* Profile Card */}
        <Card className="border-0 shadow-2xl bg-white/90 dark:bg-[#141414]/90 backdrop-blur-xl overflow-visible">
          <CardContent className="pt-6">

            {/* Name and Badges */}
            <div className="text-center mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">
                {isOwnProfile || !isFeatureEnabled('profile_lock_name') ? getFullName(displayUser) : 'Hidden'}
              </h1>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Badge className={`capitalize px-3 py-1 font-medium border ${getRoleBadgeStyle(displayUser.role)}`}>
                  {displayUser.role}
                </Badge>
                <Badge className={`px-3 py-1 font-medium border ${status.color}`}>
                  {status.label}
                </Badge>
              </div>
            </div>

            {/* Edit Form */}
            {editing ? (
              <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                {idLocked && isOwnProfile && (
                  <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200" data-testid="profile-id-locked-notice">
                    Your legal name, date of birth and address are locked from your verified state ID. Set a <strong>Preferred First Name</strong> below if you'd like a different first name shown on your business card and search results.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600 dark:text-slate-400">First Name {idLocked ? <span className="text-[10px] text-emerald-700">(locked from ID)</span> : null}</Label>
                    <Input
                      value={editData.first_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
                      disabled={idLocked && !isAdminViewing}
                      className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700"
                      data-testid="profile-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 dark:text-slate-400">Last Name {idLocked ? <span className="text-[10px] text-emerald-700">(locked from ID)</span> : null}</Label>
                    <Input
                      value={editData.last_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
                      disabled={idLocked && !isAdminViewing}
                      className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700"
                      data-testid="profile-last-name"
                    />
                  </div>
                </div>
                {/* Preferred First Name — replaces first name on outward surfaces */}
                {isOwnProfile && (
                  <div className="space-y-2">
                    <Label className="text-slate-600 dark:text-slate-400">
                      Preferred First Name <span className="text-[10px] text-slate-500">(optional — shown on your business card &amp; in search)</span>
                    </Label>
                    <Input
                      value={editData.preferred_first_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, preferred_first_name: e.target.value }))}
                      placeholder={editData.first_name || 'e.g. Alex'}
                      maxLength={60}
                      className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700"
                      data-testid="profile-preferred-first-name"
                    />
                  </div>
                )}
                {/* Custom Title — overrides the role-derived label
                    everywhere outward-facing surfaces use it (business
                    card, email signature, header). Leave empty to fall
                    back to "<State> <Role>" (e.g. "North Carolina
                    Admin"). Persists via PUT /api/auth/me. */}
                {isOwnProfile && (
                  <div className="space-y-2">
                    <Label className="text-slate-600 dark:text-slate-400">
                      Title <span className="text-[10px] text-slate-500">(optional — replaces your role label on your business card &amp; email signature)</span>
                    </Label>
                    <Input
                      value={editData.title}
                      onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder='e.g. "CEO" or "Director of Ops"'
                      maxLength={80}
                      className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700"
                      data-testid="profile-custom-title"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-slate-600 dark:text-slate-400">State</Label>
                  <Select value={editData.state} onValueChange={(v) => setEditData(prev => ({ ...prev, state: v }))}>
                    <SelectTrigger className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Industry selection (for leads and affiliates) */}
                {(displayUser.role === 'lead' || displayUser.role === 'affiliate') && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">Industry</Label>
                      <Select 
                        value={editData.industry} 
                        onValueChange={(v) => setEditData(prev => ({ ...prev, industry: v, industry_domain: '' }))}
                      >
                        <SelectTrigger className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map(ind => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Domain selection only for affiliates */}
                    {displayUser.role === 'affiliate' && editData.industry && (
                      <div className="space-y-2">
                        <Label className="text-slate-600 dark:text-slate-400">Domain</Label>
                        <Select 
                          value={editData.industry_domain} 
                          onValueChange={(v) => setEditData(prev => ({ ...prev, industry_domain: v }))}
                        >
                          <SelectTrigger className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700">
                            <SelectValue placeholder="Select domain" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableDomains.map(domain => (
                              <SelectItem key={domain} value={domain}>{domain}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
                
                {/* Contact Information Section (Own Profile Only) */}
                <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-2">Personal Information</h3>
                  <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 mb-4 text-xs text-amber-800 dark:text-amber-200">
                    <strong>Privacy:</strong> Your personal details below are only visible to company leadership — not to other leads, affiliates, or external parties.
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">Personal Phone Number</Label>
                      <Input
                        value={editData.personal_phone}
                        onChange={(e) => setEditData(prev => ({ ...prev, personal_phone: e.target.value }))}
                        placeholder="+1 (555) 123-4567"
                        className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700"
                        data-testid="profile-personal-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">Personal Email Address</Label>
                      <Input
                        value={editData.email}
                        disabled
                        className="bg-slate-100 dark:bg-[#0d0d0d] border-slate-200 dark:border-slate-700 text-muted-foreground"
                        data-testid="profile-personal-email"
                      />
                      <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">Home Address {idLocked ? <span className="text-[10px] text-emerald-700">(locked from ID)</span> : null}</Label>
                      <Input
                        value={editData.home_address}
                        onChange={(e) => setEditData(prev => ({ ...prev, home_address: e.target.value }))}
                        placeholder="Enter your home address"
                        disabled={idLocked && !isAdminViewing}
                        className="bg-slate-50 dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-700"
                        data-testid="profile-home-address"
                      />
                    </div>
                  </div>

                  {showOrbAndReferralFields && (
                  <>
                  <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mt-6 mb-3">ORB Information</h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">ORB Phone Number</Label>
                      <Input
                        value={editData.orb_phone}
                        disabled
                        placeholder="Assigned by your admin based on state"
                        className="bg-slate-100 dark:bg-[#0d0d0d] border-slate-200 dark:border-slate-700 text-muted-foreground"
                        data-testid="profile-orb-phone-readonly"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">ORB Email Address</Label>
                      <Input
                        value={editData.orb_email}
                        disabled
                        placeholder="Assigned by your admin based on state"
                        className="bg-slate-100 dark:bg-[#0d0d0d] border-slate-200 dark:border-slate-700 text-muted-foreground"
                        data-testid="profile-orb-email-readonly"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-600 dark:text-slate-400">ORB Address</Label>
                      <Input
                        value={editData.orb_address}
                        disabled
                        placeholder="Assigned by your admin based on state"
                        className="bg-slate-100 dark:bg-[#0d0d0d] border-slate-200 dark:border-slate-700 text-muted-foreground"
                        data-testid="profile-orb-address-readonly"
                      />
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Your ORB Phone, Email, and Address are tied to your state and managed by company leadership. Contact admin to change them.
                    </p>
                  </div>
                  </>
                  )}
                </div>
              </div>
            ) : (
              /* Profile Info Display */
              <>
                {/* Public Info (Outward Facing) - Controlled by profile_lock toggles */}
                <div className="flex flex-wrap items-center justify-center gap-4 py-4 border-t border-slate-100 dark:border-slate-800">
                  {displayUser.state && (isOwnProfile || !isFeatureEnabled('profile_lock_state')) && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{displayUser.state}</span>
                    </div>
                  )}
                  {displayUser.industry && (isOwnProfile || !isFeatureEnabled('profile_lock_industry')) && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                        <Briefcase className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">
                        {displayUser.industry}
                        {displayUser.industry_domain && (isOwnProfile || !isFeatureEnabled('profile_lock_domain')) ? ` • ${displayUser.industry_domain}` : ''}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Private Info (Inward Facing - Only for Own Profile) */}
                {isOwnProfile && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Personal Information</h3>
                    <p className="text-[11px] text-amber-700 dark:text-amber-300 mb-3">
                      Visible only to company leadership.
                    </p>
                    {/* iter 97 — Verified Identity card (own profile, when ID is verified) */}
                    {idLocked && (
                      <div className="mb-4 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10" data-testid="profile-verified-identity">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                          <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">Verified Identity</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div><span className="text-muted-foreground">Legal Name:</span> <span className="font-medium">{[displayUser.id_first_name, displayUser.id_middle_name, displayUser.id_last_name].filter(Boolean).join(' ')}</span></div>
                          <div><span className="text-muted-foreground">Date of Birth:</span> <span className="font-medium">{displayUser.id_dob || '—'}</span></div>
                          <div className="sm:col-span-2"><span className="text-muted-foreground">Address from ID:</span> <span className="font-medium">{[displayUser.id_address, displayUser.id_city, displayUser.id_state, displayUser.id_postal_code].filter(Boolean).join(', ') || '—'}</span></div>
                          <div><span className="text-muted-foreground">License #:</span> <span className="font-medium">{displayUser.id_license_number || '—'}</span></div>
                          <div><span className="text-muted-foreground">Verified:</span> <span className="font-medium">{displayUser.id_verified_at ? new Date(displayUser.id_verified_at).toLocaleDateString() : '—'}</span></div>
                        </div>
                        {displayUser.preferred_first_name && (
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Public surfaces show your preferred name <strong>{displayUser.preferred_first_name}</strong> instead of <strong>{displayUser.id_first_name}</strong>.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      <div className="p-3 bg-slate-50 dark:bg-[#0d0d0d] rounded-lg">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Personal Phone Number</p>
                        <p className="text-sm font-medium">{displayUser.personal_phone || 'Not set'}</p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#0d0d0d] rounded-lg">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Personal Email Address</p>
                        <p className="text-sm font-medium">{displayUser.email || 'Not set'}</p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#0d0d0d] rounded-lg sm:col-span-2">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Home Address</p>
                        <p className="text-sm font-medium">{displayUser.home_address || 'Not set'}</p>
                      </div>
                    </div>
                    {showOrbAndReferralFields && (
                    <>
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">ORB Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 dark:bg-[#0d0d0d] rounded-lg">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">ORB Phone Number</p>
                        <p className="text-sm font-medium">{displayUser.orb_phone || 'Not assigned'}</p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#0d0d0d] rounded-lg">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">ORB Email Address</p>
                        <p className="text-sm font-medium">{displayUser.orb_email || 'Not assigned'}</p>
                      </div>
                      <div className="p-3 bg-slate-50 dark:bg-[#0d0d0d] rounded-lg sm:col-span-2">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">ORB Address</p>
                        <p className="text-sm font-medium">{displayUser.orb_address || 'Not assigned'}</p>
                      </div>
                    </div>
                    </>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Stats Grid (Own Profile Only) */}
        {isOwnProfile && !editing && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl overflow-hidden group hover:scale-[1.02] transition-transform">
              <CardContent className="p-4 text-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent" />
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayUser.onboard_count || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Onboarded</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl overflow-hidden group hover:scale-[1.02] transition-transform">
              <CardContent className="p-4 text-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent" />
                <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayUser.referral_count || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Referrals</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl overflow-hidden group hover:scale-[1.02] transition-transform">
              <CardContent className="p-4 text-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent" />
                <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Gift className="w-5 h-5 text-amber-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayUser.stocks_awarded || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Stocks</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl overflow-hidden group hover:scale-[1.02] transition-transform">
              <CardContent className="p-4 text-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent" />
                <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Star className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayUser.uplift_score || 0}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Uplift Score</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Buttons - Only show for OTHER users who are onboarded */}
        {!isOwnProfile && displayUser.is_onboarded && (
          <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button 
                  className={`bg-gradient-to-r ${getRoleGradient(displayUser.role)} hover:opacity-90 text-white border-0 shadow-lg`}
                  onClick={() => setShowScheduleMeeting(true)}
                  data-testid="schedule-meeting-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Meeting
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={handleVideoCall}
                  disabled={isInCall || callState.status === CallStatus.CALLING}
                  data-testid="video-call-profile-btn"
                >
                  {callState.status === CallStatus.CALLING ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Video className="w-4 h-4 mr-2" />
                  )}
                  {callState.status === CallStatus.CALLING ? 'Calling...' : 'Video Call'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resume Section - Only visible when accessed from My Team */}
        {fromTeam && !isOwnProfile && displayUser.resume_url && (
          <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl mt-4" data-testid="resume-section">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold">Resume</h3>
                  <p className="text-xs text-muted-foreground">Uploaded resume document</p>
                </div>
              </div>
              
              {displayUser.resume_url.startsWith('data:application/pdf') ? (
                <div className="space-y-3">
                  {/* PDF Preview using object tag (doesn't auto-download on iOS) */}
                  <object
                    data={displayUser.resume_url}
                    type="application/pdf"
                    className="w-full h-96 border rounded-lg bg-white"
                  >
                    <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
                      <FileText className="w-12 h-12 mb-3" />
                      <p className="text-sm">PDF preview not available in your browser</p>
                      <p className="text-xs">Click Download to view the resume</p>
                    </div>
                  </object>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = displayUser.resume_url;
                      link.download = `resume_${getFullName(displayUser).replace(/\s+/g, '_')}.pdf`;
                      link.click();
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Resume preview not available</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Referral ID — own profile only, anchored at the bottom */}
        {isOwnProfile && showOrbAndReferralFields && displayUser.referral_id && (
          <Card className="border-0 shadow-lg bg-white/80 dark:bg-[#141414]/80 backdrop-blur-xl mt-4" data-testid="referral-id-section">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#586D52]/10 rounded-xl flex items-center justify-center">
                  <Hash className="w-5 h-5 text-[#586D52]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-white">Your Referral ID</h3>
                  <p className="text-xs text-muted-foreground">Permanent code attached to your card and ORB download link.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded-lg bg-slate-100 dark:bg-[#1f1f1f] text-base font-mono tracking-widest text-slate-800 dark:text-slate-200 select-all" data-testid="referral-id-value">
                  {displayUser.referral_id}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(displayUser.referral_id);
                      toast.success('Referral ID copied');
                    } catch (_e) {
                      toast.error('Copy failed');
                    }
                  }}
                  data-testid="referral-id-copy-btn"
                  aria-label="Copy referral ID"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sign Out — only on the OWN profile, anchored at the bottom.
            Replaces the Sign Out item that used to live in the
            desktop hamburger / mobile menu (iter140). */}
        {isOwnProfile && (
          <div className="mt-6 mb-2 flex justify-center" data-testid="profile-signout-wrapper">
            <Button
              variant="outline"
              onClick={() => { try { logout(); } catch (e) { /* ignore */ } }}
              className="text-destructive hover:bg-destructive/10 border-destructive/30 px-6"
              data-testid="profile-signout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        )}
      </div>
      {/* Mounted unconditionally so the dialog can open from anywhere
          on the profile (button or future deep-links). */}
      <ScheduleMeetingDialog
        api={api}
        open={showScheduleMeeting}
        onOpenChange={setShowScheduleMeeting}
        targetUser={displayUser}
      />
    </div>
  );
};

export default ProfilePage;
